import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import { SiteWithTemplate } from '../types/template';
import { isTransitionalStatus, SITES_LIST_POLL_MS } from '../constants/siteStatus';
import { api, isApiClientError } from '../api/sdk';
import {
  buildListQuery,
  DEFAULT_PER_PAGE,
  type Paginated,
  type PaginationParams,
} from '../api/pagination';

/** Polling interval (ms) used while a site is in a transitional status. */
const SITE_DETAIL_POLL_MS = 5_000;

export interface SubdomainCheckResult {
  available: boolean;
  subdomain: string;
  message: string;
}

/**
 * Check if a subdomain is available
 */
export const checkSubdomainAvailability = async (
  subdomain: string
): Promise<SubdomainCheckResult> => {
  if (!subdomain || subdomain.length < 3) {
    return {
      available: false,
      subdomain: subdomain,
      message: 'Subdomain must be at least 3 characters'
    };
  }

  return api.get<SubdomainCheckResult>(
    `/api/sites/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`
  );
};

/**
 * Hook for checking subdomain availability with debouncing
 * Handles ApiClientError for better error messages
 */
export const useSubdomainCheck = (debounceMs: number = 400) => {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<SubdomainCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isSdkReady } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Monotonic sequence so only the response for the most recent check (or
  // none, after unmount) is allowed to touch state — out-of-order responses
  // must not display stale availability for a different subdomain.
  const requestSeqRef = useRef(0);

  const checkSubdomain = useCallback((subdomain: string) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Invalidate any in-flight request
    requestSeqRef.current++;

    // Reset state for empty/short input
    if (!subdomain || subdomain.length < 3) {
      setResult(subdomain ? {
        available: false,
        subdomain: subdomain,
        message: 'Subdomain must be at least 3 characters'
      } : null);
      setChecking(false);
      return;
    }

    if (!isAuthenticated || !isSdkReady) {
      setError(!isAuthenticated ? 'Not authenticated' : 'SDK not ready');
      return;
    }

    setChecking(true);
    setError(null);
    setResult(null);

    // Debounce the API call
    timeoutRef.current = setTimeout(async () => {
      const seq = ++requestSeqRef.current;

      try {
        const checkResult = await checkSubdomainAvailability(subdomain);
        if (seq !== requestSeqRef.current) return;
        setResult(checkResult);
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setError(isApiClientError(err) ? err.message : (err instanceof Error ? err.message : 'An error occurred'));
      } finally {
        if (seq === requestSeqRef.current) setChecking(false);
      }
    }, debounceMs);
  }, [isAuthenticated, isSdkReady, debounceMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Invalidate any in-flight request so it can't set state post-unmount.
      // The lint rule guards DOM refs going stale; this is a counter and
      // bumping the live value is exactly the point.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      requestSeqRef.current++;
    };
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setChecking(false);
  }, []);

  return {
    checking,
    result,
    error,
    checkSubdomain,
    reset
  };
};

/**
 * Hook for fetching the user's sites list. Backed by TanStack Query under the
 * `['sites']` key. The backend serializes each site's template summary
 * (`templateKey`, `displayName`, `tier`) directly on the row — no separate
 * template fetch from the frontend.
 *
 * Polls every `SITES_LIST_POLL_MS` while any visible site is in a transitional
 * status (publishing/destroying), matching the cadence of the pre-tanstack
 * hook.
 */
export const useSites = (params: PaginationParams = {}) => {
  const { isAuthenticated, isSdkReady } = useAuth();
  const page = params.page ?? 1;
  const perPage = params.perPage ?? DEFAULT_PER_PAGE;

  return useQuery<Paginated<SiteWithTemplate>>({
    queryKey: ['sites', { page, perPage }],
    queryFn: async () => {
      const result = await api.get<Paginated<any>>(
        `/api/sites${buildListQuery({ page, perPage })}`,
      );
      return {
        items: result.items.map((site): SiteWithTemplate => ({
          id: site.id,
          organizationId: site.organizationId || 'default-org',
          templateCatalogId: site.templateId,
          config: site.configuration || {},
          status: site.status,
          createdAt: site.createdAt,
          updatedAt: site.updatedAt,
          subdomain: site.subdomain,
          url: site.url,
          stripeSubscriptionId: site.stripeSubscriptionId,
          subscriptionStatus: site.subscriptionStatus,
          subscriptionCancelAtPeriodEnd: site.subscriptionCancelAtPeriodEnd,
          template: site.template ?? null,
        })),
        pagination: result.pagination,
      };
    },
    enabled: isAuthenticated && isSdkReady,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      if (!items?.length) return false;
      return items.some((s) => isTransitionalStatus(s.status))
        ? SITES_LIST_POLL_MS
        : false;
    },
    refetchIntervalInBackground: false,
  });
};

/**
 * Hook for fetching a single site detail row. Backed by TanStack Query under
 * `['sites', siteId]`. Polls every `SITE_DETAIL_POLL_MS` while the site is in
 * a transitional status (publishing/destroying); the webhook handler advances
 * the row's status as workflow events arrive.
 *
 * The detail endpoint is the only source that includes per-site billing
 * fields (`paymentRequired`, `subscriptionCurrentPeriodEnd`, `suspendedAt`,
 * `lastError*`) and the inlined template summary — so we always fetch fresh
 * rather than read from the list cache.
 */
export const useSite = (siteId: string) => {
  const { isAuthenticated, isSdkReady } = useAuth();

  return useQuery<SiteWithTemplate>({
    queryKey: ['sites', siteId],
    queryFn: async () => {
      const apiSite = await api.get<any>(`/api/sites/${encodeURIComponent(siteId)}`);
      return {
        id: apiSite.id,
        organizationId: apiSite.organizationId || 'default-org',
        templateCatalogId: apiSite.templateId,
        config: apiSite.configuration || {},
        status: apiSite.status,
        createdAt: apiSite.createdAt,
        updatedAt: apiSite.updatedAt,
        // Surface the per-site subscription fields the API returns. SiteDetail
        // drives all subscription UX off these without a second fetch.
        subdomain: apiSite.subdomain,
        url: apiSite.url,
        stripeSubscriptionId: apiSite.stripeSubscriptionId ?? null,
        subscriptionStatus: apiSite.subscriptionStatus,
        subscriptionCurrentPeriodEnd: apiSite.subscriptionCurrentPeriodEnd ?? null,
        subscriptionCancelAtPeriodEnd: apiSite.subscriptionCancelAtPeriodEnd ?? false,
        paymentRequired: apiSite.paymentRequired ?? false,
        suspendedAt: apiSite.suspendedAt ?? null,
        offlineAt: apiSite.offlineAt ?? null,
        lastError: apiSite.lastError ?? null,
        lastErrorStep: apiSite.lastErrorStep ?? null,
        lastErrorAt: apiSite.lastErrorAt ?? null,
        template: apiSite.template ?? null,
      };
    },
    enabled: !!siteId && isAuthenticated && isSdkReady,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && isTransitionalStatus(status) ? SITE_DETAIL_POLL_MS : false;
    },
    refetchIntervalInBackground: false,
  });
};

